import { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import { useCompanySettings, useUpdateCompanySettings } from '@/api/queries/settings.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

export default function AttendancePoliciesSettingsPage() {
  const { data } = useCompanySettings();
  const { mutate, isPending } = useUpdateCompanySettings();

  const [form, setForm] = useState({
    grace_period: '15', half_day_after: '4', work_hours: '9',
    week_offs: ['Saturday','Sunday'], late_deduction: true, lop_on_excess_absent: true,
  });

  useEffect(() => {
    const p = data?.data?.settings?.attendance_policy;
    if (p) {
      setForm({
        grace_period: String(p.grace_period_minutes ?? 15),
        half_day_after: String(p.half_day_after_hours ?? 4),
        work_hours: String(p.work_hours_per_day ?? 9),
        week_offs: p.week_offs ?? ['Saturday','Sunday'],
        late_deduction: p.late_deduction ?? true,
        lop_on_excess_absent: p.lop_on_excess_absent ?? true,
      });
    }
  }, [data]);

  const s = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inputCls = "w-full h-10 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]";

  const handleSave = () => {
    mutate({
      settings: {
        attendance_policy: {
          grace_period_minutes: Number(form.grace_period),
          half_day_after_hours: Number(form.half_day_after),
          work_hours_per_day: Number(form.work_hours),
          week_offs: form.week_offs,
          late_deduction: form.late_deduction,
          lop_on_excess_absent: form.lop_on_excess_absent,
        },
      },
    }, {
      onSuccess: () => toast.success('Attendance policies saved'),
      onError: () => toast.error('Failed to save'),
    });
  };

  return (
    <div className="space-y-5 fade-in max-w-xl">
      <PageHeader title="Attendance Policies" breadcrumb={[{ label: 'Settings', href: '/settings' }, { label: 'Attendance Policies' }]}/>
      <div className="card space-y-5 p-5">
        <div className="grid grid-cols-2 gap-4">
          {[['Grace Period (mins)', 'grace_period'],['Half Day Under (hours)', 'half_day_after'],['Work Hours/Day', 'work_hours']].map(([label, key]) => (
            <div key={key} className="space-y-1.5">
              <label className="text-[13px] font-semibold text-[var(--text-primary)]">{label}</label>
              <input type="number" min="0" value={form[key]} onChange={e => s(key, e.target.value)} className={inputCls}/>
            </div>
          ))}
        </div>
        <div className="space-y-2">
          <label className="text-[13px] font-semibold text-[var(--text-primary)]">Week Offs</label>
          <div className="flex flex-wrap gap-2">
            {DAYS.map(day => (
              <button key={day} onClick={() => s('week_offs', form.week_offs.includes(day) ? form.week_offs.filter(d => d !== day) : [...form.week_offs, day])}
                className={`px-3 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${form.week_offs.includes(day) ? 'bg-[var(--accent)] text-white' : 'bg-[var(--bg-muted)] text-[var(--text-secondary)]'}`}>
                {day.slice(0,3)}
              </button>
            ))}
          </div>
        </div>
        {[['late_deduction','Deduct salary for late arrivals'],['lop_on_excess_absent','Apply LOP on excess absences']].map(([key, label]) => (
          <div key={key} className="flex items-center justify-between p-3 bg-[var(--bg-muted)] rounded-[10px]">
            <p className="text-[13px] font-semibold text-[var(--text-primary)]">{label}</p>
            <button onClick={() => s(key, !form[key])}
              className={`w-11 h-6 rounded-full transition-colors relative ${form[key] ? 'bg-[var(--accent)]' : 'bg-[var(--border)]'}`}>
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${form[key] ? 'left-5' : 'left-0.5'}`}/>
            </button>
          </div>
        ))}
      </div>
      <Button icon={Save} onClick={handleSave} disabled={isPending}>{isPending ? 'Saving…' : 'Save Policies'}</Button>
    </div>
  );
}
